import { loadStripe } from '@stripe/stripe-js';
import { auth } from '../config/firebase';

// Suppress Stripe HTTP warning in development
if (process.env.NODE_ENV === 'development') {
  const originalWarn = console.warn;
  console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].includes('You may test your Stripe.js integration over HTTP')) {
      return; // Suppress this specific warning
    }
    originalWarn.apply(console, args);
  };
}

// Determine which Stripe key to use based on mode
const getStripeKey = () => {
  const mode = process.env.REACT_APP_STRIPE_MODE || 'live';
  const key = mode === 'test'
    ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST
    : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE;

  // console.log('🔑 Stripe Mode:', mode);
  // console.log('🔑 Using Stripe Key:', key?.substring(0, 20) + '...');

  return key;
};

// Lazy-load Stripe SDK — only downloads 217KB when a function actually needs it
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = loadStripe(getStripeKey());
  return stripePromise;
};

/**
 * Create a Stripe checkout session
 * @param {string} priceId - The Stripe price ID for the product
 * @param {string} successUrl - URL to redirect to after successful payment
 * @param {string} cancelUrl - URL to redirect to if payment is cancelled
 * @returns {Promise<{sessionId: string, url: string}>}
 */
export const createCheckoutSession = async (priceId, successUrl, cancelUrl, mode = 'payment', premiumTier = null) => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();

    const body = {
      priceId,
      successUrl,
      cancelUrl,
      email: user.email,
      mode
    };
    if (premiumTier) {
      body.premiumTier = premiumTier;
    }

    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createCheckoutSession', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Redirect to Stripe Checkout
 * @param {string} sessionId - The checkout session ID
 */
export const redirectToCheckout = async (sessionId) => {
  const stripe = await getStripe();
  const { error } = await stripe.redirectToCheckout({ sessionId });
  
  if (error) {
    throw error;
  }
};

/**
 * Check if user can create more events based on their subscription
 * @param {Object} userProfile - The user's profile data
 * @param {number} currentEventCount - Current number of events the user has
 * @returns {boolean} - Whether the user can create more events
 */
export const canCreateMoreEvents = (userProfile, currentEventCount) => {
  if (!userProfile) return false;

  // Tournament creation is only gated by the 8888 portal code (learned on calls)
  // Users can create 1 tournament max (enforced by UI hiding the button after first creation)
  return currentEventCount < 1;
};

/**
 * Get the appropriate message when user hits event limit
 * @param {Object} userProfile - The user's profile data
 * @returns {string} - Message to display
 */
export const getEventLimitMessage = (userProfile) => {
  return "Contact support to create additional tournaments.";
};

// Mode is controlled by REACT_APP_STRIPE_MODE in .env (default: live)
const stripeMode = process.env.REACT_APP_STRIPE_MODE || 'live';
const isTestMode = stripeMode === 'test';

// Log current mode for debugging
// console.log(`Stripe Mode: ${stripeMode}, Test Mode: ${isTestMode}`);

/**
 * Stripe Price IDs for different subscription tiers
 * These are loaded from environment variables based on test/live mode
 * 
 * MONTHLY: Standard $9.99/month premium subscription (Master tier)
 * PRO_MONTHLY: Pro $29.99/month subscription with reduced application fees
 * ONETIME: One-time payment option (if needed)
 */
export const PRICE_IDS = {
  MONTHLY: isTestMode
    ? process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID_TEST
    : process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID_LIVE,
  // Pro tier: $29.99/month with reduced application fees for tournament registrations
  PRO_MONTHLY: isTestMode
    ? process.env.REACT_APP_STRIPE_PRO_MONTHLY_PRICE_ID_TEST
    : process.env.REACT_APP_STRIPE_PRO_MONTHLY_PRICE_ID_LIVE,
  // Annual tiers — undefined until price IDs are added to .env.local
  ANNUAL: isTestMode
    ? process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID_TEST
    : process.env.REACT_APP_STRIPE_ANNUAL_PRICE_ID_LIVE,
  PRO_ANNUAL: isTestMode
    ? process.env.REACT_APP_STRIPE_PRO_ANNUAL_PRICE_ID_TEST
    : process.env.REACT_APP_STRIPE_PRO_ANNUAL_PRICE_ID_LIVE,
  ONETIME: isTestMode
    ? process.env.REACT_APP_STRIPE_ONETIME_PRICE_ID_TEST
    : process.env.REACT_APP_STRIPE_ONETIME_PRICE_ID_LIVE
};

/**
 * Create a registration checkout session for tournament payments
 * @param {Object} registrationData - Registration payment data
 * @returns {Promise<{sessionId: string, url: string}>}
 */
export const createRegistrationCheckoutSession = async (registrationData) => {
  try {
    // Get current user's ID token (optional for guest payments)
    const user = auth.currentUser;
    let headers = {
      'Content-Type': 'application/json'
    };
    
    // Add authorization header only if user is authenticated
    if (user) {
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createRegistrationCheckoutSession', {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(registrationData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a retry checkout session for existing participant
 * @param {Object} retryData - Retry payment data
 * @returns {Promise<{sessionId: string, url: string}>}
 */
export const createRetryCheckoutSession = async (retryData) => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const idToken = await user.getIdToken();
    
    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createRetryCheckoutSession', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(retryData)
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Redirect to Stripe Checkout for registration payment
 * @param {string} sessionId - The checkout session ID
 */
export const redirectToRegistrationCheckout = async (sessionId) => {
  const stripe = await getStripe();
  const { error } = await stripe.redirectToCheckout({ sessionId });
  
  if (error) {
    throw error;
  }
};

/**
 * Create a Stripe billing portal session
 * @param {string} returnUrl - URL to return to after portal session
 * @returns {Promise<{url: string}>}
 */
export const createPortalSession = async (returnUrl = window.location.origin + '/account') => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();

    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createPortalSession', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        returnUrl
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a donation checkout session
 * Platform fee: Flat 5%
 * @param {string} profileId - Recipient's profile ID
 * @param {number} amount - Amount in cents (minimum $2 = 200 cents)
 * @param {string} donorEmail - Optional donor email
 * @param {string} donorMessage - Optional donor message (max 500 chars)
 * @returns {Promise<{sessionUrl: string}>}
 */
export const createDonationCheckoutSession = async (profileId, amount, donorEmail = '', donorMessage = '') => {
  try {
    // Optional auth (donations work for guests too)
    const user = auth.currentUser;
    const headers = {
      'Content-Type': 'application/json'
    };

    if (user) {
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createDonationCheckoutSession', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        profileId,
        amount,
        donorEmail,
        donorMessage,
        origin: window.location.origin
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.details || errorData.error || 'Failed to create donation session');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create an Individual Session (1-on-1) checkout session
 * @param {Object} bookingData - Booking information
 * @param {string} bookingData.coachId - Coach's Firestore document ID
 * @param {string} bookingData.lessonId - Session document ID
 * @param {number} bookingData.slotIndex - Index of the slot to book
 * @param {number} bookingData.priceUsd - Price in USD
 * @param {string} [bookingData.successUrl] - Optional success redirect URL
 * @param {string} [bookingData.cancelUrl] - Optional cancel redirect URL
 * @returns {Promise<{success: boolean, sessionId: string, url: string}>}
 */
export const createIndividualSessionCheckoutSession = async (bookingData) => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();

    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createIndividualSessionCheckoutSession', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(bookingData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Cancel an individual session booking and process refund
 * @param {Object} cancellationData - Cancellation information
 * @param {string} cancellationData.lessonId - Session document ID
 * @param {number} cancellationData.slotIndex - Index of the slot to cancel
 * @returns {Promise<{success: boolean, message: string, refund?: Object}>}
 */
export const cancelIndividualSessionAndRefund = async (cancellationData) => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();

    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/cancelIndividualSessionAndRefund', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(cancellationData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Create a Group Chess credit purchase checkout session
 * @param {Object} purchaseData - Purchase information
 * @param {string} purchaseData.coachId - Coach's Firestore document ID
 * @param {string} purchaseData.packageType - '1', '4', or '7' (number of credits)
 * @param {number} purchaseData.priceUsd - Price in USD
 * @param {string} [purchaseData.successUrl] - Optional success redirect URL
 * @param {string} [purchaseData.cancelUrl] - Optional cancel redirect URL
 * @returns {Promise<{success: boolean, sessionId: string, url: string}>}
 */
export const createGroupChessCheckoutSession = async (purchaseData) => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();

    const response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/createGroupChessCheckoutSession', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(purchaseData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Process a refund for a tournament registration payment
 * @param {Object} refundData - Refund information
 * @param {string} refundData.eventId - Event ID
 * @param {string} refundData.participantId - Participant ID
 * @param {number} refundData.adminFee - Admin fee in dollars (organizer keeps this)
 * @param {string} refundData.reason - Reason for refund
 * @param {string} refundData.notes - Additional notes
 * @returns {Promise<{success: boolean, refund: Object}>}
 */
export const processRefund = async (refundData) => {
  try {
    // Get current user's ID token
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated. Please log in and try again.');
    }

    const idToken = await user.getIdToken();

    let response;
    try {
      response = await fetch('https://us-central1-chess-web-host.cloudfunctions.net/processStripeRefund', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(refundData)
      });
    } catch (networkError) {
      // Network-level error (no response received)
      throw new Error(`Network error: Unable to reach refund server. Please check your internet connection and try again. (${networkError.message})`);
    }

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `Server error (${response.status})`;
      } catch {
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }
      throw new Error(`Refund failed: ${errorMessage}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};